import { Component, ElementRef, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ApiError } from '../../../../core/errors/api-error';
import { MessageService } from '../../../../core/services/message/message.service';
import { Cidade } from '../../../../home/cidade/cidade';
import { CidadeCrudHttpService } from '../../../../home/cidade/services/cidade-crud-http.service';
import { CidadeTypeaheadHttpService } from '../../../../home/cidade/typeahead/cidade-typeahead-http.service';
import { ICidade } from '../../../../home/cidade/typeahead/cidade.interface';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { IAutocompleteSettings } from '../autocomplete/interface/settings.interface';

@Component({
  selector: 'sgt-novo-typeahead-cidade',
  templateUrl: './novo-typeahead-cidade.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NovoTypeaheadCidadeComponent),
      multi: true,
    },
  ],
})
export class NovoTypeaheadCidadeComponent extends CustomControlValueAccessor<ICidade> {
  cidades: ICidade[] = [];
  selectedCidade: ICidade | null;
  settings: IAutocompleteSettings = {
    placeholder: 'Digite o nome da cidade',
    noResultsText: 'Nenhuma cidade encontrada',
    disabled: false,
    isLoading: false,
    showBeforeContent: true,
    isEmpty: null
  };

  constructor(
    private el: ElementRef,
    private messageService: MessageService,
    private cidadeTypeaheadHttpService: CidadeTypeaheadHttpService,
    private cidadeCrudHttpService: CidadeCrudHttpService,
  ) {
    super();
  }

  setFocus(): void {
    const input = this.el.nativeElement.querySelector('input');
    if (input) {
      input.focus();
    }
  }

  fetchCidades(searchText: string) {
    this.settings.isLoading = true;
    this.cidadeTypeaheadHttpService
      .search(searchText, undefined, undefined, undefined, 1000)
      .subscribe(
        (entities: Cidade[]) => {
          this.cidades = this.transformPayloadToAutocompleteItens(entities);
          this.setAutoCompleteSettings();
        },
        (error: ApiError) =>
          this.messageService.error(
            error.message,
            'Erro ao pesquisar as cidades',
            error
          )
      );
  }

  setAutoCompleteSettings(): void {
    this.settings.isEmpty = this.cidades.length === 0;
    this.settings.isOpenDropdown = this.cidades.length > 0;
    this.settings.isLoading = false;

    if (this.settings.isEmpty) {
      this.selectedCidade = null;
    }
  }

  transformPayloadToAutocompleteItens(cidades: Cidade[]): ICidade[] {
    return cidades.map((cidade) => ({
      id: cidade.id,
      nome: cidade.nome,
      uf: cidade.estado?.uf
    }));
  }

  onSearchCidade(searchText: string): void {
    if (searchText && searchText.length) {
      this.fetchCidades(searchText);
      return;
    }

    this.cidades = [];
    this.selectedCidade = null;
    this.settings.isOpenDropdown = false;
    this.emitChange(null);
  }

  onSelectCidade(cidade: ICidade): void {
    this.selectedCidade = cidade;
    this.settings.isOpenDropdown = false;
    this.cidades = [];
    this.emitChange(cidade);
  }

  protected updateValue(newValue: ICidade | null): void {
    this.selectedCidade = newValue;
    if (!newValue) {
      this.settings = { ...this.settings, clearFields: true };
      return;
    }
    if (!newValue.nome && newValue.id) {
      this.buscaCidadeById(newValue.id);
    }
  }

  private buscaCidadeById(id: number): void {
    this.cidadeCrudHttpService.find(id).subscribe({
      next: (cidade) => {
        this.selectedCidade = {
          id: cidade.id,
          nome: cidade.nome,
          uf: cidade.estado?.uf,
        };
      },
      error: (error: ApiError) => {
        this.messageService.error(
          error.message,
          'Erro ao carregar cidade',
          error
        );
      },
    });
  }
}
